import React, { FC } from 'react';
import { NavLink, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { Collapse } from 'reactstrap';
import { gqlArtist, gqlProfileStatusEnum as PROFILE_STATUS } from '@minecraft.graphql-types';
import { AccountArtistFragment } from '@minecraft.graphql-operations';
import { useUserContext } from '@blocs.auth';
import { useTranslation, Trans } from '@blocs.i18n';
import { useBillingPopup, PaywallLocation } from '@blocs.paywall';
import { WindowWidthContext } from '@minecraft.utils';
import DropdownMenu from '@minecraft.dropdown-menu';
import DropdownToggle from '@minecraft.dropdown-toggle';
import UncontrolledDropdown from '@minecraft.uncontrolled-dropdown';
import Nav from '@minecraft.nav';
import { ProfileQueries } from '@minecraft.shared-queries';
import { setLatestArtist } from '@consumer-talent.lib/localAuthStore';
import { INFO_MAX_LENGTH } from '@consumer-talent.config/constants/profile';
import SCREEN_NAMES from '@consumer-talent.config/constants/screenNames';
import routeHelper from '@consumer-talent.utils/routeHelper';
import { useArtistContext } from '@consumer-talent.components/core/ArtistContext';
import AccountInfo from '@consumer-talent.components/core/AccountInfo';
import ArtistsInfo from '@consumer-talent.components/Artists/ArtistsInfo';
import ArtistsList from './ArtistList';
import MainNavMenu from './MainNavMenu';
import CurrentArtistSubMenu from './CurrentArtistSubMenu';
import MobileMenuWithCurrentArtist from './MobileMenuWithCurrentArtist';
import MobileMenuWithOutCurrentArtist from './MobileMenuWithOutCurrentArtist';
import { UpgradeMembershipButton } from './UpgradeMembershipButton';

import {
  NavGlobalStyle,
  DropDownMenuItemMobile,
  MenuItem,
  CurrentArtistInfo,
  MenuButtonMobile,
  MenuButton,
  MenuItemMobile,
  StyledNav,
  MobileCollapseMenuWrapper,
  NavAndButtonWrapper,
} from './Styles';

type Props = {
  isOpen: boolean;
  handleLogout: () => void;
};

/* HELPERS */
const AllowedNavProfileStatuses: string[] = [PROFILE_STATUS.ACTIVE, PROFILE_STATUS.INACTIVE];

const NavBetween = styled(Nav)`
  justify-content: space-between;
`;

const StyledCollapse = styled(Collapse)`
  justify-content: space-between;
`;

const NavLoggedIn: FC<Props> = ({ isOpen, handleLogout }) => {
  const { t } = useTranslation();
  const { myAccount, subscription } = useUserContext();
  const { currentArtist, currentProfile, toggleArtist, artistId } = useArtistContext();
  const [setUpdatedProfileField] = useMutation(ProfileQueries.SET_UPDATED_PROFILE_FIELD);
  const billingPopup = useBillingPopup();
  const widthData = WindowWidthContext.useWidthData();

  const { id, artists, firstName, lastName } = myAccount;

  const handleArtistSelect = (artist: AccountArtistFragment | gqlArtist): void => {
    setLatestArtist(id, artist.artistId);

    toggleArtist(artist.artistId);
  };

  const accountSettingsLink = routeHelper.accountDetails.link();
  const alertsCount = 0;
  const profile = currentProfile;
  const notificationLink =
    artists?.length === 1
      ? routeHelper.artistNotificationSettings.forLink(artistId)
      : routeHelper.notificationSettings.forLink();
  const openBilling = () => {
    billingPopup.show({
      location: PaywallLocation.GLOBAL_NAV,
    });
  };
  const upgradeOnTop = (widthData.width as number) <= 767;

  return (
    <MobileCollapseMenuWrapper>
      <StyledCollapse isOpen={isOpen} navbar>
        <NavGlobalStyle />
        <NavBetween navbar>
          {upgradeOnTop && <UpgradeMembershipButton openBilling={openBilling} />}
          {/* here we should check for profileStatusCode from context to avoid showing navigation for artists which doesn't have any subscription */}
          {subscription && profile && AllowedNavProfileStatuses.includes(profile.profileStatus.code) && (
            <MainNavMenu profile={profile} setUpdatedProfileField={setUpdatedProfileField} alertsCount={alertsCount} />
          )}
          {isOpen && (
            <>
              {currentArtist && (
                <>
                  <hr />
                  <MenuButtonMobile data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-current-artist`} selected>
                    <ArtistsInfo artist={currentArtist} maxLength={INFO_MAX_LENGTH} showInfo={false} />
                  </MenuButtonMobile>
                </>
              )}
              <ArtistsList
                artists={artists}
                selected={currentArtist}
                Component={MenuItemMobile}
                isMobile
                handleArtistSelect={handleArtistSelect}
              />
              <hr />
              <DropDownMenuItemMobile>
                {currentArtist ? (
                  <MobileMenuWithCurrentArtist
                    notificationLink={notificationLink}
                    accountSettingsLink={accountSettingsLink}
                  />
                ) : (
                  <MobileMenuWithOutCurrentArtist
                    accountSettingsLink={accountSettingsLink}
                    notificationLink={notificationLink}
                  />
                )}
              </DropDownMenuItemMobile>
              <MenuButtonMobile data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-log-out`} onClick={handleLogout}>
                {t('common:mainNav.logOut')}
              </MenuButtonMobile>
            </>
          )}
        </NavBetween>
        <NavAndButtonWrapper>
          {!upgradeOnTop && <UpgradeMembershipButton openBilling={openBilling} />}
          <StyledNav navbar className="ml-auto">
            {!isOpen && (
              <>
                <UncontrolledDropdown nav inNavbar direction="down">
                  <DropdownToggle nav>
                    <CurrentArtistInfo
                      data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-${
                        currentArtist ? 'current-artist' : 'account-info'
                      }`}
                      data-testid="main-nav-selected-artist"
                    >
                      {currentArtist ? (
                        <ArtistsInfo artist={currentArtist} showInfo={false} maxLength={14} />
                      ) : (
                        <AccountInfo firstName={firstName} lastName={lastName} />
                      )}
                    </CurrentArtistInfo>
                  </DropdownToggle>
                  <DropdownMenu data-testid="main-nav-dropdown-menu">
                    <ArtistsList
                      artists={artists}
                      selected={currentArtist}
                      Component={MenuItem as FC<{ $selected?: boolean }>}
                      isMobile={false}
                      handleArtistSelect={handleArtistSelect}
                    />
                    {currentArtist ? (
                      <CurrentArtistSubMenu
                        accountSettingsLink={accountSettingsLink}
                        notificationLink={notificationLink}
                      />
                    ) : (
                      <>
                        {' '}
                        <MenuItem component="span">
                          <Link
                            data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-account-settings`}
                            to={accountSettingsLink}
                          >
                            <Trans i18nKey="common:accountSettingsMenu.accountSettings" components={[<></>]} />
                          </Link>
                        </MenuItem>
                        <MenuItem component="span">
                          <NavLink
                            {...notificationLink}
                            data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-notification-settings`}
                            role="button"
                          >
                            {t('common:accountSettingsMenu.notificationSettings')}
                          </NavLink>
                        </MenuItem>
                      </>
                    )}
                    <MenuButton data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-log-out`} onClick={handleLogout}>
                      {t('common:mainNav.logOut')}
                    </MenuButton>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </>
            )}
          </StyledNav>
        </NavAndButtonWrapper>
      </StyledCollapse>
    </MobileCollapseMenuWrapper>
  );
};

export default NavLoggedIn;
